/**
 * QMS Comments Hooks
 * React Query hooks for ticket comments
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchQmsComments, addQmsComment, editQmsComment, deleteQmsComment } from '../api/qmsApi';
import type { QmsComment } from '../types/qms.types';
import { qmsKeys } from './useQmsBoard';

export const qmsCommentsKeys = {
  all: ['qms', 'comments'] as const,
  ticket: (ticketId: string) => [...qmsCommentsKeys.all, ticketId] as const,
};

/**
 * Hook to fetch comments for a ticket
 */
export function useQmsComments(ticketId: string) {
  return useQuery<QmsComment[]>({
    queryKey: qmsCommentsKeys.ticket(ticketId),
    queryFn: () => fetchQmsComments(ticketId),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Hook to add a comment to a ticket
 */
export function useAddQmsComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, content }: { ticketId: string; content: string }) =>
      addQmsComment(ticketId, content),
    onSuccess: (_, variables) => {
      // Invalidate comments for this ticket
      queryClient.invalidateQueries({
        queryKey: qmsCommentsKeys.ticket(variables.ticketId),
      });
      // Also invalidate timeline if it exists
      queryClient.invalidateQueries({
        queryKey: qmsKeys.timeline(variables.ticketId),
      });
    },
  });
}

/**
 * Hook to edit a comment
 */
export function useEditQmsComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, noteId, content }: { ticketId: string; noteId: string; content: string }) =>
      editQmsComment(ticketId, noteId, content),
    onSuccess: (_, variables) => {
      // Invalidate comments for this ticket
      queryClient.invalidateQueries({
        queryKey: qmsCommentsKeys.ticket(variables.ticketId),
      });
      // Also invalidate timeline to show edit activity
      queryClient.invalidateQueries({
        queryKey: qmsKeys.timeline(variables.ticketId),
      });
    },
  });
}

/**
 * Hook to delete a comment
 */
export function useDeleteQmsComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, noteId }: { ticketId: string; noteId: string }) =>
      deleteQmsComment(ticketId, noteId),
    onSuccess: (_, variables) => {
      // Invalidate comments for this ticket
      queryClient.invalidateQueries({
        queryKey: qmsCommentsKeys.ticket(variables.ticketId),
      });
      // Also invalidate timeline to show delete activity
      queryClient.invalidateQueries({
        queryKey: qmsKeys.timeline(variables.ticketId),
      });
    },
  });
}
